import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy
} from '@angular/core';
import { first, Subject, takeUntil } from 'rxjs';
import { NavController } from '@ionic/angular';
import { ActivatedRoute } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import {
  DocTypes,
  FileNode,
  FileService,
  NodeType
} from '../../services/file.service';
import { UploadDocumentsService } from '../../services/docs-service/upload-documents.service';
import { ErrorToastService } from '../../services/error-toast.service';
import * as Sentry from '@sentry/angular';
import { FilesHelperService } from '../../services/files-helper.service';

// Состояние поручения экспедитору на полке документов:
// - waiting — черновик сформирован, ждём подпись АГЕНТА (встречная подпись экспедитора не требуется);
// - signed  — поручение подписано агентом: можно скачать подписанный документ и продолжать отгрузку;
// - hidden  — поручения ещё нет (или не применимо) — блок не показываем.
type ForwarderAssignmentState = 'waiting' | 'signed' | 'hidden';

@Component({
  selector: 'app-document-shelf',
  templateUrl: './document-shelf.component.html',
  styleUrls: ['./document-shelf.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentShelfComponent implements OnChanges, OnDestroy {
  @Input() bargainId: number;
  // Явный триггер обновления полки из родителя (days-list). Полка живёт под *ngIf="!loading" и при
  // pull-to-refresh пересоздаётся, но после фоновой загрузки документов (isUpdatingAfterUpload) loading
  // не тогглится — тогда обновляемся по смене этого сигнала (ngOnChanges), а не по пересозданию.
  @Input() refreshSignal?: number;
  public assignmentState: ForwarderAssignmentState = 'hidden';
  public signedAssignmentFile?: FileNode;
  public checkingStatus = false;
  public fileCount: number;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private fileService: FileService,
    private navCtrl: NavController,
    private cd: ChangeDetectorRef,
    private route: ActivatedRoute,
    private uploadDocumentsService: UploadDocumentsService,
    private errorToastService: ErrorToastService,
    private filesHelper: FilesHelperService
  ) {}

  ngOnChanges() {
    this.refreshDocuments();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Скачать/открыть подписанное поручение (на нативной платформе — открыть; в вебе файл уже скачан).
  public async downloadSignedAssignment() {
    if (!this.signedAssignmentFile?.documentId) {
      return;
    }
    try {
      const filePath = await this.fileService.downloadFileForDeal(
        this.signedAssignmentFile.documentId,
        this.signedAssignmentFile.name,
        this.signedAssignmentFile.documentType
      );

      if (filePath && Capacitor.isNativePlatform()) {
        const extension =
          this.signedAssignmentFile.name.toLowerCase().split('.').pop() || '';
        const mimeType = this.filesHelper.getMimeTypeFromExtension(extension);
        await this.filesHelper.openFile(filePath, mimeType);
      }
    } catch (error) {
      Sentry.captureException(error);
      this.errorToastService.showError('Не удалось открыть файл');
    }
  }

  public async shareSignedAssignment() {
    if (!this.signedAssignmentFile?.documentId) {
      return;
    }
    try {
      const filePath = await this.fileService.downloadFileForDeal(
        this.signedAssignmentFile.documentId,
        this.signedAssignmentFile.name,
        this.signedAssignmentFile.documentType
      );

      if (filePath && Capacitor.isNativePlatform()) {
        await this.filesHelper.shareFile(
          filePath,
          this.signedAssignmentFile.name
        );
      }
    } catch (error) {
      Sentry.captureException(error);
      this.errorToastService.showError('Не удалось поделиться файлом');
    }
  }

  // Точечная проверка статуса подписи без полной перезагрузки экрана (поллинга в приложении нет —
  // обновляем только по действию пользователя либо через pull-to-refresh родительского экрана).
  public checkStatus() {
    if (!this.bargainId || this.checkingStatus) {
      return;
    }
    this.checkingStatus = true;
    this.cd.markForCheck();
    this.fileService
      .getDealFiles(this.bargainId)
      .pipe(first(), takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          this.applyNodes(res.data.nodes);
          this.checkingStatus = false;
          this.cd.markForCheck();
        },
        error: error => {
          Sentry.captureException(error);
          this.errorToastService.showError('Не удалось проверить статус');
          this.checkingStatus = false;
          this.cd.markForCheck();
        }
      });
  }

  public navigateToFileList() {
    this.navCtrl
      .navigateForward([`files/${this.bargainId}`], {
        relativeTo: this.route
      })
      .then();
  }

  private refreshDocuments(): void {
    if (this.bargainId) {
      this.fileService
        .getDealFiles(this.bargainId)
        .pipe(first())
        .subscribe(res => {
          this.applyNodes(res.data.nodes);
          this.cd.markForCheck();
        });
    }
  }

  // Состояние поручения по документам заявки: подписанное имеет приоритет над черновиком.
  private applyNodes(nodes: FileNode[]): void {
    this.fileCount = this.countDocuments(nodes);
    const signed = this.findNodeByType(
      nodes,
      DocTypes.SIGNED_FORWARDER_ASSIGNMENT
    );
    const draft = this.findNodeByType(
      nodes,
      DocTypes.FORWARDER_ASSIGNMENT_TEMPLATE
    );
    if (signed) {
      this.assignmentState = 'signed';
      this.signedAssignmentFile = signed;
    } else if (draft) {
      this.assignmentState = 'waiting';
      this.signedAssignmentFile = undefined;
    } else {
      this.assignmentState = 'hidden';
      this.signedAssignmentFile = undefined;
    }
  }

  // Рекурсивный поиск документа по типу: документы поручения — уровня сделки (обычно в корне дерева),
  // но при группировке (по дате/ТС) могут оказаться вложены в node.items — ищем и там, чтобы состояние
  // не зависало на 'waiting' после подписи, если документ вложен.
  private findNodeByType(
    nodes: FileNode[],
    documentType: DocTypes
  ): FileNode | undefined {
    for (const node of nodes) {
      if (node.documentType === documentType) {
        return node;
      }
      if (node.items) {
        const found = this.findNodeByType(node.items, documentType);
        if (found) {
          return found;
        }
      }
    }
    return undefined;
  }

  private countDocuments(nodes: FileNode[]): number {
    return nodes.reduce((acc, node) => {
      let count = acc;
      if (node.nodeType === NodeType.DOCUMENT) {
        count += 1;
      }
      if (node.items) {
        count += this.countDocuments(node.items);
      }
      return count;
    }, 0);
  }
}
